import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getOverviewStats } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { IssueFinder } from "../components/IssueFinder";
import { FreeUpdates } from "../components/FreeUpdates";
import {
  LayoutDashboard, LayoutPanelLeft, ShieldCheck, Cpu, KeyRound, ShieldAlert, Siren,
  MonitorPlay, LifeBuoy, Gauge, ClipboardList, Wrench, Bot, Cloud, Route, LayoutGrid,
  Rocket, Bookmark, Wrench as Tools2, Building2, ArrowRight, Sparkles,
} from "lucide-react";

const TROUBLESHOOT = [
  { slug: "diagnostics", to: "/diagnostics", icon: LayoutDashboard, title: "Diagnostics", desc: "Guided health-check wizards for common Cisco UC issues." },
  { slug: "site360", to: "/site360", icon: LayoutPanelLeft, title: "Site 360", desc: "Full operational view of any single location." },
  { slug: "health", to: "/health-checks", icon: ShieldCheck, title: "Health Checks", desc: "Run and track recurring UC health checks." },
  { slug: "firmware", to: "/firmware", icon: Cpu, title: "Firmware Lookup", desc: "Phone firmware loads, recommendations & known issues." },
  { slug: "licenses", to: "/licenses", icon: KeyRound, title: "Licenses", desc: "Smart/PLM license tracking and renewals." },
  { slug: "certificates", to: "/certificates", icon: ShieldAlert, title: "Certificates", desc: "Certificate expiry tracking across all nodes." },
  { slug: "e911", to: "/e911", icon: Siren, title: "E911 Compliance", desc: "Emergency locations & ELIN compliance." },
  { slug: "rooms", to: "/rooms", icon: MonitorPlay, title: "Room Collab", desc: "Webex & room device readiness." },
  { slug: "dr", to: "/dr", icon: LifeBuoy, title: "DR & Failover", desc: "Disaster recovery and failover planning." },
  { slug: "qos", to: "/qos", icon: Gauge, title: "QoS Builder", desc: "Build QoS policies for voice and video." },
  { slug: "changes", to: "/changes", icon: ClipboardList, title: "Change Log", desc: "Track configuration changes and approvals." },
  { slug: "tools", to: "/tools", icon: Wrench, title: "Tools", desc: "Bulk MAC parser and field utilities." },
  { slug: "assistant", to: "/assistant", icon: Bot, title: "AI Assistant", desc: "Ask the Cisco UC migration AI." },
];

const MIGRATION = [
  { slug: "migration", to: "/migration", icon: Cloud, title: "Cloud Migration", desc: "Plan your locations and cloud migration." },
  { slug: "runbook", to: "/runbook", icon: Route, title: "Migration Runbook", desc: "Gated PPDIO runbooks — Webex Calling & CUCM 15." },
  { slug: "devices", to: "/runbook", icon: Cpu, title: "Device Compatibility", desc: "Check phones against CUCM 15 / Webex before cutover." },
  { slug: "scoreboard", to: "/scoreboard", icon: LayoutGrid, title: "Estate Scoreboard", desc: "Estate-wide migration & upgrade status grid." },
  { slug: "waves", to: "/waves", icon: Rocket, title: "Wave Cutover", desc: "Plan and run migration waves to cutover." },
  { slug: "reports", to: "/reports", icon: Bookmark, title: "Reports", desc: "Saved reports, audits and PDF exports." },
];

const STATS = [
  { key: "sites", label: "Sites", tone: "text-blue-400" },
  { key: "open_gates", label: "Open gates", tone: "text-violet-400" },
  { key: "device_blockers", label: "Device blockers", tone: "text-red-400" },
  { key: "certs_expiring", label: "Certs expiring", tone: "text-amber-400" },
  { key: "licenses_expiring", label: "Licenses due", tone: "text-amber-400" },
  { key: "e911_gaps", label: "E911 gaps", tone: "text-red-400" },
];

const Card = ({ item, onGo }) => (
  <button
    data-testid={`overview-card-${item.slug}`}
    onClick={() => onGo(item.to)}
    className="group text-left rounded-xl border border-zinc-800 bg-zinc-900 hover:border-blue-600/50 hover:bg-zinc-900/60 p-5 transition-all"
  >
    <div className="flex items-center justify-between">
      <div className="h-10 w-10 rounded-lg bg-blue-600/15 border border-blue-600/25 flex items-center justify-center">
        <item.icon className="h-5 w-5 text-blue-400" />
      </div>
      <ArrowRight className="h-4 w-4 text-zinc-600 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
    </div>
    <div className="mt-3 text-[15px] font-bold text-white">{item.title}</div>
    <div className="mt-1 text-[13px] text-zinc-400 leading-snug">{item.desc}</div>
  </button>
);

export default function Overview() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState("migration");
  const [stats, setStats] = useState(null);

  useEffect(() => { getOverviewStats().then(setStats).catch(() => setStats(null)); }, []);

  const items = tab === "migration" ? MIGRATION : TROUBLESHOOT;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> overview
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-6xl" data-testid="overview-page">
        {/* Hero */}
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <Building2 className="h-4 w-4" /> UC Engineer Pro
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mt-2">
          {user?.name ? `Welcome back, ${user.name.split(" ")[0]}.` : "Your Cisco UC command center."}
        </h1>
        <p className="text-zinc-400 mt-3 max-w-2xl text-base">
          One workspace for the whole estate — <span className="text-zinc-200">troubleshoot & operate</span> your Cisco UC environment,
          and <span className="text-zinc-200">migrate & upgrade</span> to Webex Calling or CUCM 15. Pick a side below to jump in.
        </p>

        <div className="mt-6"><IssueFinder /></div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-6">
          {STATS.map((s) => (
            <div key={s.key} data-testid={`overview-stat-${s.key}`} className="rounded-lg border border-zinc-800 bg-zinc-900 p-4">
              <div className={`font-heading text-3xl font-extrabold ${s.tone}`}>{stats ? (stats[s.key] ?? 0) : "—"}</div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Toggle */}
        <div className="mt-8 inline-flex rounded-lg border border-zinc-800 bg-zinc-900 p-1" data-testid="overview-toggle">
          <button onClick={() => setTab("migration")} data-testid="tab-migration"
            className={`flex items-center gap-2 text-[13px] font-semibold px-4 py-2 rounded-md transition-colors ${tab === "migration" ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}>
            <Rocket className="h-4 w-4" /> Cloud Migration & Upgrades
          </button>
          <button onClick={() => setTab("troubleshoot")} data-testid="tab-troubleshooting"
            className={`flex items-center gap-2 text-[13px] font-semibold px-4 py-2 rounded-md transition-colors ${tab === "troubleshoot" ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"}`}>
            <Wrench className="h-4 w-4" /> Troubleshooting & Operations
          </button>
        </div>

        <div className="mt-2 text-[13px] text-zinc-500 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5 text-blue-400" />
          {tab === "migration"
            ? "Everything you need to plan, gate and execute a migration or CUCM 15 upgrade."
            : "Day-to-day diagnostics, compliance and operational tooling for your UC estate."}
        </div>

        {/* Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
          {items.map((item) => <Card key={item.slug} item={item} onGo={navigate} />)}
        </div>

        <div className="mt-8"><FreeUpdates compact /></div>
      </div>
    </div>
  );
}
